import React, { useState, lazy, Suspense, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  Home as HomeIcon,
  User,
  HelpCircle,
  Gem,
  MessageCircle,
  DollarSign,
  Wallet,
  ChevronDown,
  Dice1,
  Joystick,
  Store,
  LogOut,
  BadgePercent,
  Settings,
  Bell,
  Search,
  X,
  ArrowLeft,
  Swords,
  MessageCircleMore,
  Download,
  MessageSquareMore,
} from "lucide-react";
import { MdContacts, MdSupport } from "react-icons/md";

// Import the non-lazy loaded components first
import useUserData from "../../Hooks/useUserData";
import MessageAlerts from "../../Utils/MessageAlerts";
import VipPhoneModal from "../Vip/VipPhoneModal";
import AddToHomeBanner from "../../Config/AddToHomeBanner";
import VipGrupoWhatsApp from "../Vip/VipGrupoWhatsApp";
import NotificationModal from "../../Components/NotificationModal";
import DeportivosProApp from "../../info/DeportivosProApp";
import SportsAcademy from "../academy/SportsAcademy";
import AlertPayPromo from "../Free/AlerPayPromo";
import FreeTrialRequest from "../Free/FreeTrialRequest";
import FreePay from "../Banner/HomeFree";
import MyBalance from "../../Utils/MyBalance";
import { FaBlog } from "react-icons/fa";
import AlertsUsers from "../../Utils/AlertsUsers";
import { BsAndroid } from "react-icons/bs";
import DownloadPage from "../android/DownloadPage";

// Lazy loaded components for better performance
const FootballLoading = lazy(() => import("../../Utils/FootballLoading"));
const UserProfile = lazy(() => import("../Profile/UserProfile"));
const SupportChat = lazy(() => import("../Chat/Chat"));
const BetFree = lazy(() => import("../Free/BetFree"));
const InviteGrupo = lazy(() => import("../World/InviteGrupo"));
const BetHistory = lazy(() => import("../Free/BetHistory"));
const ContactForm = lazy(() => import("../Contact/ContactForm"));
const Marketing = lazy(() => import("../Marketing/Marketing"));
const AppAnality = lazy(() => import("../../Config/AppAnality"));
const CircularProgressReward = lazy(() =>
  import("../../Utils/CircularProgressReward")
);
const WalletView = lazy(() => import("../Wallet/WalletView"));
const HomeVip = lazy(() => import("../Vip/HomeVip"));
const HomeCourse = lazy(() => import("../media/HomeCourse"));
const CasinoPage = lazy(() => import("../casino/CasinoPage"));
const GameOne = lazy(() => import("../casino/games/GameOne"));
const url_image =
  "https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40";

const handleHistory = () => {
  window.location.href = "/prediccion-zone/history";
};
const handleSoporte = () => {
  window.location.href = "/support";
};

// Improved loading fallback with animation
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full min-h-[300px]">
    <div className="flex flex-col items-center">
      <div className="animate-pulse flex space-x-3">
        <div
          className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "0ms" }}
        ></div>
        <div
          className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "150ms" }}
        ></div>
        <div
          className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "300ms" }}
        ></div>
      </div>
      <p className="text-gray-400 mt-4 font-medium">Cargando...</p>
    </div>
  </div>
);

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentView, setCurrentView] = useState("home");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { userData, loading, error } = useUserData();
  const navigate = useNavigate();
  const url_store = "https://deportivosblogs.online/tienda/";
  const url_blog = "https://deportivosblogs.online";
  const url_support = "https://deportivospro.online/support";

  const [notificacionesActivas, setNotificacionesActivas] = useState(false);

  // ✅ Restaurar estado de notificaciones desde localStorage al cargar
  useEffect(() => {
    const estadoGuardado = localStorage.getItem("notificaciones_activadas");
    if (estadoGuardado === "true") {
      setNotificacionesActivas(true);
    }
  }, []);

  // ✅ Detectar redimensionamiento de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  // ✅ Comprobar si es dispositivo móvil
  const esMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  // ✅ Manejar clic en el botón de notificaciones
  const handleAlert = () => {
    // Si es dispositivo móvil, usar un enfoque diferente
    if (esMobile()) {
      if (notificacionesActivas) {
        setNotificacionesActivas(false);
        localStorage.setItem("notificaciones_activadas", "false");
        alert("Has desactivado las alertas.");
        return;
      } else {
        setNotificacionesActivas(true);
        localStorage.setItem("notificaciones_activadas", "true");
        alert(
          "Has activado las alertas. Recibirás actualizaciones importantes."
        );
        return;
      }
    }

    // Continuar con la lógica para escritorio
    if (!("Notification" in window)) {
      alert("Este navegador no soporta notificaciones.");
      return;
    }

    if (notificacionesActivas) {
      setNotificacionesActivas(false);
      localStorage.setItem("notificaciones_activadas", "false");
      return;
    }

    if (Notification.permission === "granted") {
      setNotificacionesActivas(true);
      localStorage.setItem("notificaciones_activadas", "true");
      new Notification("¡Notificaciones activadas!");
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          setNotificacionesActivas(true);
          localStorage.setItem("notificaciones_activadas", "true");
          new Notification("¡Notificaciones activadas con éxito!");
        } else {
          alert("No se otorgó permiso para notificaciones.");
        }
      });
    } else {
      alert(
        "Has bloqueado las notificaciones en el navegador. Verifica la configuración de tu navegador."
      );
    }
  };

  // Handle external navigation
  const handleExternalNavigation = (url) => {
    window.location.href = url;
  };

  // Handle for store
  const handleStore = () => {
    window.location.href = url_store;
  };
  const handleBlog = () => {
    window.location.href = url_blog;
  };
  const handleSupport = () => {
    window.location.href = url_support;
  };

  // Handle logout
  const handleLogout = () => {
    // Implement logout logic
    alert("Sesión cerrada correctamente");
    navigate("/prediccion-zone/start");
  };
  const handleMelBet = () => {
    window.location.href =
      "https://refpaikgai.top/L?tag=d_4386553m_59389c_&site=4386553&ad=59389";
  };

  if (loading) return <FootballLoading />;
  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="bg-gray-800/80 backdrop-blur-sm p-6 rounded-lg shadow-xl border border-gray-700 max-w-md w-full">
          <div className="text-red-500 text-xl mb-4 font-bold">
            Error al cargar datos
          </div>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium px-4 py-2 rounded-md hover:brightness-110 transition-all w-full"
          >
            Reintentar
          </button>
        </div>
      </div>
    );

  const MenuItem = ({ icon: Icon, text, view, badge, subItems }) => {
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const handleClick = () => {
      if (subItems) {
        setIsSubMenuOpen(!isSubMenuOpen);
      } else if (view === "store") {
        handleStore();
      } else if (view === "blogs") {
        handleExternalNavigation("https://deportivosblogs.online");
      } else if (view === "dp-shorts") {
        handleExternalNavigation("/shorts");
      } else {
        setCurrentView(view);
        if (isMobile) setIsMenuOpen(false);
      }
    };

    return (
      <li>
        <div
          className={`flex items-center justify-between rounded-lg transition-all duration-200 cursor-pointer ${
            currentView === view
              ? "bg-indigo-600/20 text-white border-l-4 border-indigo-500"
              : "hover:bg-gray-700/40 text-gray-300"
          }`}
          onClick={handleClick}
        >
          <div className="flex items-center w-full p-3">
            <Icon
              className={`${
                currentView === view ? "text-indigo-400" : "text-gray-400"
              } transition-colors w-5 h-5`}
            />
            <span
              className={`ml-3 ${
                currentView === view
                  ? "text-white font-semibold"
                  : "text-gray-300"
              } transition-colors`}
            >
              {text}
            </span>
            {badge && <div className="ml-auto flex items-center">{badge}</div>}
            {subItems && (
              <ChevronDown
                className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${
                  isSubMenuOpen ? "rotate-180" : ""
                }`}
              />
            )}
          </div>
        </div>

        {isSubMenuOpen && subItems && (
          <ul
            className="pl-6 mt-1 space-y-1 overflow-hidden"
            style={{ animation: "fadeIn 0.3s" }}
          >
            {subItems.map((subItem, index) => (
              <li
                key={index}
                className={`flex items-center p-2 rounded-md cursor-pointer hover:bg-gray-700/50 transition-colors ${
                  currentView === subItem.view
                    ? "bg-gray-700/70 border-l-2 border-indigo-500"
                    : ""
                }`}
                onClick={() => {
                  setCurrentView(subItem.view);
                  if (isMobile) setIsMenuOpen(false);
                }}
                style={{
                  animation: "slideIn 0.3s",
                  animationDelay: `${index * 0.05}s`,
                }}
              >
                <subItem.icon
                  className={`w-4 h-4 ${
                    currentView === subItem.view
                      ? "text-indigo-400"
                      : "text-gray-400"
                  }`}
                />
                <span
                  className={`ml-3 ${
                    currentView === subItem.view
                      ? "text-white"
                      : "text-gray-300"
                  }`}
                >
                  {subItem.text}
                </span>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const renderContent = () => {
    return (
      <Suspense fallback={<LoadingFallback />}>
        {(() => {
          switch (currentView) {
            case "profile":
              return <UserProfile />;
            case "help":
              return <SupportChat />;
            case "bet":
              return <BetFree limit={userData ? undefined : 3} />;
            case "vip":
              return <HomeVip />;
            case "whatsapp":
              return <InviteGrupo />;
            case "history":
              return <BetHistory />;
            case "contact":
              return <ContactForm />;
            case "analitys":
              return <AppAnality />;
            case "win-pay":
              return <Marketing />;
            case "download":
              return <DownloadPage />;
            case "wallet":
              return <WalletView />;
            case "homeCasino":
              return <CasinoPage />;
            case "DeportivosProApp":
              return <DeportivosProApp />;
            case "academy":
              return <SportsAcademy />;
            case "depbonus":
              return <GameOne />;
            case "FreeTrial":
              return <FreeTrialRequest />;
            default:
              return (
                <>
                  <BetFree limit={userData ? undefined : 3} />
                  {!userData && (
                    <div
                      className="fixed bottom-0 left-0 w-full bg-gray-800/95 backdrop-blur-sm shadow-lg flex flex-col items-center z-40 border-t border-gray-700"
                      style={{ animation: "slideUpFade 0.5s" }}
                    >
                      <div className="w-full max-w-lg mx-auto py-4 px-4">
                        <p className="text-gray-200 mb-3 text-base font-medium text-center">
                          Inicie sesión o cree una cuenta para ver más
                          pronósticos
                        </p>
                        <div className="flex justify-center gap-3">
                          <Link to="/prediccion-zone/start">
                            <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium px-5 py-2.5 rounded-lg hover:brightness-110 transition-all shadow-lg shadow-blue-600/20">
                              Iniciar sesión
                            </button>
                          </Link>
                          <Link to="/prediccion-zone/join">
                            <button className="bg-gradient-to-r from-emerald-600 to-green-600 text-white font-medium px-5 py-2.5 rounded-lg hover:brightness-110 transition-all shadow-lg shadow-green-600/20">
                              Crear cuenta
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
          }
        })()}
      </Suspense>
    );
  };

  // Theme color classes for consistent styling
  const themeColors = {
    primary: "from-indigo-600 to-blue-700",
    secondary: "from-emerald-600 to-green-600",
    accent: "from-amber-500 to-orange-600",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-indigo-950">
      {/* Navbar */}
      <nav className="fixed top-0 z-50 w-full bg-gray-900/90 backdrop-blur-md border-b border-gray-800 shadow-xl">
        <div className="h-16 flex items-center justify-between w-full px-3 md:px-4 max-w-full mx-auto">
          <div className="flex items-center">
            {isMobile && currentView !== "home" ? (
              <button
                onClick={() => setCurrentView("home")}
                className="p-2 text-gray-400 hover:text-white rounded-md hover:bg-gray-700/50 transition-colors mr-1"
                aria-label="Back to home"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 text-gray-400 hover:text-white rounded-md hover:bg-gray-700/50 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            )}

            <div className="flex items-center ml-2">
              <div className=" rounded-lg p-1.5 shadow-lg shadow-indigo-500/30">
                {/* <Gem className="h-5 w-5 text-white" /> */}
                <img className="h-5 w-5 text-white" src={url_image} alt="" />
              </div>
              <span className="ml-2 text-white font-bold text-lg hidden sm:block">
                DeportivosPro
              </span>
            </div>

            <div className="ml-3 lg:ml-6">
              <MyBalance />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* <Link
              to="/support"
              className="relative text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-800 transition-colors"
              aria-label="Support"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 border-2 border-gray-900 text-xs flex items-center justify-center" />
            </Link> */}

            <NotificationModal />

            <div className="relative user-menu-container">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center ml-1"
                aria-label="User menu"
              >
                <div className="relative">
                  <img
                    src={
                      userData?.avatar ||
                      "https://avatar.iran.liara.run/public/boy"
                    }
                    alt="Profile"
                    className="h-8 w-8 rounded-full ring-2 ring-indigo-500 cursor-pointer hover:brightness-110 transition-all object-cover"
                  />
                  <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-1 ring-gray-900" />
                </div>
              </button>

              {isUserMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-72 bg-gray-900 rounded-xl shadow-2xl border border-gray-800 overflow-hidden z-50"
                  style={{ animation: "slideDownFade 0.3s" }}
                >
                  <div className="p-4 border-b border-gray-800 bg-gradient-to-b from-indigo-900/30 to-gray-900">
                    <div className="flex items-center">
                      <img
                        src={
                          userData?.avatar ||
                          "https://avatar.iran.liara.run/public/boy"
                        }
                        alt="Profile"
                        className="h-14 w-14 rounded-full ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/20"
                      />
                      <div className="ml-3">
                        <p className="text-white font-medium text-lg">
                          {userData?.name || "Usuario"}
                        </p>
                        <p className="text-gray-400 text-sm truncate">
                          {userData?.email || "usuario@ejemplo.com"}
                        </p>
                        <div className="mt-1 flex items-center">
                          <div className="h-2 w-2 rounded-full bg-emerald-500 mr-2"></div>
                          <p className="text-gray-400 text-xs">
                            {userData?.typeU || "Cuenta Gratis"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-800/50 grid grid-cols-3 gap-2">
                      <div
                        onClick={() => {
                          setCurrentView("whatsapp");
                          setIsUserMenuOpen(false);
                        }}
                        className="flex flex-col items-center justify-center p-2 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                      >
                        <MessageCircleMore className="h-5 w-5 text-indigo-400 mb-1" />
                        <span className="text-xs text-gray-300">Grupo</span>
                      </div>
                      <div
                        onClick={handleBlog}
                        className="flex flex-col items-center justify-center p-2 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                      >
                        <FaBlog className="h-5 w-5 text-indigo-400 mb-1" />
                        <span className="text-xs text-gray-300">Blog</span>
                      </div>
                      <div
                        onClick={handleAlert}
                        className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors cursor-pointer
    ${
      notificacionesActivas
        ? "bg-green-600 hover:bg-green-700"
        : "bg-gray-800/50 hover:bg-gray-800"
    }`}
                      >
                        <Bell
                          className={`h-5 w-5 mb-1 ${
                            notificacionesActivas
                              ? "text-white"
                              : "text-indigo-400"
                          }`}
                        />
                        <span
                          className={`text-xs ${
                            notificacionesActivas
                              ? "text-white"
                              : "text-gray-300"
                          }`}
                        >
                          {esMobile() ? "Alertas" : "Notificaciones"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="py-1">
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                      onClick={() => {
                        setCurrentView("profile");
                        setIsUserMenuOpen(false);
                      }}
                    >
                      <User className="h-4 w-4 mr-3 text-indigo-400" />
                      Mi perfil
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                      onClick={() => {
                        setCurrentView("wallet");
                        setIsUserMenuOpen(false);
                      }}
                    >
                      <Wallet className="h-4 w-4 mr-3 text-indigo-400" />
                      Mi billetera
                    </button>
                    <button
                      className="flex items-center w-full px-4 py-2.5 text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Layout */}
      <div className="pt-16 lg:grid lg:grid-cols-[280px,1fr]">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-64 sm:w-68 md:w-72 transform bg-gray-900/80 backdrop-blur-lg pt-16 transition-transform duration-300 border-r border-gray-800 lg:static lg:translate-x-0 ${
            isMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto sidebar-scrollbar px-2 sm:px-3 py-3">
              {/* User profile summary in sidebar (for logged-in users) */}
              {userData && (
                <div className="mb-4 sm:mb-6 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 sm:p-4 border border-gray-800 shadow-lg">
                  <div className="flex items-center">
                    <img
                      src={
                        userData?.avatar ||
                        "https://avatar.iran.liara.run/public/boy"
                      }
                      alt="Profile"
                      className="h-10 w-10 sm:h-12 sm:w-12 rounded-full ring-2 ring-indigo-500 shadow-lg"
                    />
                    <div className="ml-2 sm:ml-3">
                      <p className="text-white text-sm sm:text-base font-medium">
                        {userData?.name || "Usuario"}
                      </p>
                      <div className="flex items-center mt-1">
                        <div className="h-2 w-2 rounded-full bg-emerald-500 mr-2"></div>
                        <p className="text-gray-400 text-xs">
                          {userData?.typeU || "Cuenta Gratis"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <nav className="pr-1">
                <div className="px-2 py-2 mb-1">
                  <h3 className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                    Navegación principal
                  </h3>
                </div>
                <ul className="space-y-1 sm:space-y-2">
                  <MenuItem icon={HomeIcon} text="Inicio" view="home" />

                  <MenuItem
                    icon={Gem}
                    text="VIP"
                    view="vip"
                    badge={
                      <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium shadow-inner shadow-green-500/20">
                        +99%🔥
                      </span>
                    }
                  />

                  <MenuItem
                    icon={BadgePercent}
                    text="Prueba Gratis"
                    view="FreeTrial"
                    badge={
                      <span className="bg-gradient-to-r from-blue-600 to-amber-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        Gratis
                      </span>
                    }
                  />
                  <MenuItem
                    icon={DollarSign}
                    text="Gana dinero"
                    view="win-pay"
                    badge={
                      <span className="bg-gradient-to-r from-green-600 to-amber-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        🤑💲
                      </span>
                    }
                  />
                  {/* <MenuItem
                    icon={BsAndroid}
                    text="Descargar"
                    view="download"
                    badge={
                      <span className="bg-gradient-to-r from-purple-600 to-amber-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        <Download />
                      </span>
                    }
                  /> */}
                </ul>

                <div className="px-2 py-2 mt-4 sm:mt-6 mb-1">
                  <h3 className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                    Servicios
                  </h3>
                </div>
                <ul className="space-y-1 sm:space-y-2">
                  <div onClick={handleStore}>
                    <MenuItem
                      icon={Store}
                      text="Tienda"
                      badge={
                        <span className="bg-gradient-to-r from-red-600 to-amber-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                          🛒
                        </span>
                      }
                    />
                  </div>

                  <MenuItem icon={Wallet} text="Billetera" view="wallet" />
                  <MenuItem
                    icon={Joystick}
                    text="Casino"
                    view="homeCasino"
                    badge={
                      <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        Nuevo
                      </span>
                    }
                    subItems={[
                      { icon: Dice1, text: "DepBonus", view: "depbonus" },
                    ]}
                  />
                  <MenuItem
                    icon={MessageCircle}
                    text="Grupo WhatsApp"
                    view="whatsapp"
                  />
                </ul>

                <div className="px-2 py-2 mt-4 sm:mt-6 mb-1">
                  <h3 className="text-xs uppercase text-gray-500 font-bold tracking-wider">
                    Cuenta
                  </h3>
                </div>
                <ul className="space-y-1 sm:space-y-2">
                  <MenuItem icon={User} text="Perfil" view="profile" />
                  <MenuItem
                    icon={MdContacts}
                    text="Contacto"
                    view="contact"
                    badge={
                      <span className="bg-gradient-to-r from-orange-600 to-amber-600 text-xs rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium">
                        Nuevo
                      </span>
                    }
                  />
                  <MenuItem
                    icon={HelpCircle}
                    text="Mensajes"
                    view="help"
                    badge={
                      <span className="text-xs text-gray-400 bg-gray-700/50 px-2 py-0.5 rounded-md">
                        FAQ
                      </span>
                    }
                  />
                </ul>
              </nav>
            </div>
            <div className="p-2 sm:p-3 border-t border-gray-800">
              <FreePay />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-transparent px-2 py-3 md:px-4 md:py-4 main-content-scrollbar">
          {/* Page Header Based on Current View */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">
                {(() => {
                  switch (currentView) {
                    case "home":
                      return "Pronósticos de Hoy";
                    case "profile":
                      return "Mi Perfil";
                    case "help":
                      return "Centro de Ayuda";
                    case "vip":
                      return "Membresía VIP";
                    case "whatsapp":
                      return "Grupo de WhatsApp";
                    case "wallet":
                      return "Mi Billetera";
                    case "homeCasino":
                      return "Casino DeportivosPro";
                    case "FreeTrial":
                      return "Prueba VIP";
                    case "win-pay":
                      return "Ganar Dinero";
                    case "FreeTrial":
                      return "Prueba Gratuita";
                    case "contact":
                      return "Contacto";
                    case "depbonus":
                      return "DepBonus - Juego";
                    default:
                      return "DeportivosPro";
                  }
                })()}
              </h1>
              <p className="text-gray-400 text-sm hidden md:block">
                {(() => {
                  switch (currentView) {
                    case "home":
                      return "Explora los mejores pronósticos deportivos del día";
                    case "profile":
                      return "Gestiona tu cuenta y preferencias";
                    case "help":
                      return "Soporte y respuestas a tus preguntas";
                    case "vip":
                      return "Accede a pronósticos exclusivos y premium";
                    case "whatsapp":
                      return "Únete a nuestra comunidad de apostadores";
                    case "wallet":
                      return "Administra tus fondos y transacciones";
                    case "homeCasino":
                      return "Disfruta de nuestros juegos y gana premios";
                    case "FreeTrial":
                      return "Pide ahora tu prueba del Vip";
                    case "win-pay":
                      return "Aprovecha nuestro programa de afiliados";
                    case "FreeTrial":
                      return "Prueba nuestros servicios premium sin costo";
                    case "contact":
                      return "Ponte en contacto con nuestro equipo";
                    case "depbonus":
                      return "Juega y gana bonos exclusivos";
                    default:
                      return "Tu plataforma de pronósticos deportivos";
                  }
                })()}
              </p>
            </div>

            {currentView === "home" && (
              <div className="flex items-center space-x-2">
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3 py-1.5 rounded-lg text-sm transition-all shadow-lg shadow-indigo-600/20 flex items-center">
                  <span className="mr-1">Hoy</span>
                </button>
                <button
                  onClick={handleHistory}
                  className="bg-gray-800/60 hover:bg-gray-700 text-gray-300 font-medium px-3 py-1.5 rounded-lg text-sm transition-all"
                >
                  <span>Historial</span>
                </button>
                <button
                  onClick={handleSoporte}
                  className="bg-gray-800/60 hover:bg-gray-700 text-gray-300 font-medium px-3 py-1.5 rounded-lg text-sm transition-all"
                >
                  <span>Soporte</span>
                </button>
              </div>
            )}
          </div>

          <div className="w-full">{renderContent()}</div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-gray-900/80 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMenuOpen(false)}
          style={{ animation: "fadeIn 0.2s" }}
        />
      )}

      {/* Fixed Bottom Navigation for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 shadow-lg z-40">
        <div className="flex justify-around items-center h-16">
          <button
            onClick={() => {
              setCurrentView("home");
              setIsMenuOpen(false);
            }}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "home" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <HomeIcon className="w-5 h-5 mb-1" />
            <span className="text-xs">Inicio</span>
          </button>

          <button
            onClick={() => {
              setCurrentView("vip");
              setIsMenuOpen(false);
            }}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "vip" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <Gem className="w-5 h-5 mb-1" />
            <span className="text-xs">VIP</span>
          </button>

          {/* <button
            onClick={() => {
              setCurrentView("win-pay");
              setIsMenuOpen(false);
            }}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "win-pay" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <DollarSign className="w-5 h-5 mb-1" />
            <span className="text-xs">Ganar</span>
          </button> */}
          <button
            onClick={handleMelBet}
            className={
              "flex flex-col items-center justify-center px-2 py-1 w-1/5"
            }
          >
            <DollarSign className="w-5 h-5 mb-1" />
            <span className="text-xs">Bono</span>
          </button>

          <button
            onClick={() => {
              setCurrentView("FreeTrial");
              setIsMenuOpen(false);
            }}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "FreeTrial" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <Swords className="w-5 h-5 mb-1" />
            <span className="text-xs">Prueba</span>
          </button>

          <button
            onClick={handleSupport}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "profile" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <MessageSquareMore className="w-5 h-5 mb-1" />
            <span className="text-xs">Chat</span>
          </button>
          {/* <button
            onClick={() => {
              setCurrentView("profile");
              setIsMenuOpen(false);
            }}
            className={`flex flex-col items-center justify-center px-2 py-1 w-1/5 ${
              currentView === "profile" ? "text-indigo-400" : "text-gray-400"
            }`}
          >
            <User className="w-5 h-5 mb-1" />
            <span className="text-xs">Perfil</span>
          </button> */}
        </div>
      </div>

      {/* Global components */}
      <VipGrupoWhatsApp />
      <AddToHomeBanner />
      <VipPhoneModal />
      <MessageAlerts />
      <AlertsUsers />

      {/* Login CTA for non-authenticated users */}
      {!userData && currentView !== "FreeTrial" && (
        <div
          className="fixed bottom-16 lg:bottom-0 left-0 w-full bg-gradient-to-t from-gray-900 to-gray-900/95 backdrop-blur-sm shadow-lg flex flex-col items-center z-40 border-t border-gray-800"
          style={{ animation: "slideUpFade 0.5s" }}
        >
          <div className="w-full max-w-lg mx-auto py-4 px-4">
            <p className="text-gray-200 mb-3 text-base font-medium text-center">
              Inicie sesión o cree una cuenta para desbloquear todas las
              funciones
            </p>
            <div className="flex justify-center gap-3">
              <Link to="/prediccion-zone/start">
                <button className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium px-5 py-2.5 rounded-lg hover:brightness-110 transition-all shadow-lg shadow-indigo-600/20 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Iniciar sesión
                </button>
              </Link>
              <Link to="/prediccion-zone/join">
                <button className="bg-gradient-to-r from-emerald-600 to-green-600 text-white font-medium px-5 py-2.5 rounded-lg hover:brightness-110 transition-all shadow-lg shadow-green-600/20 flex items-center">
                  <BadgePercent className="h-4 w-4 mr-2" />
                  Crear cuenta
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(-10px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideUpFade {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideDownFade {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .sidebar-scrollbar::-webkit-scrollbar {
          width: 5px;
        }

        .sidebar-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.1);
        }

        .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(79, 70, 229, 0.3);
          border-radius: 20px;
        }

        .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(79, 70, 229, 0.5);
        }

        .main-content-scrollbar::-webkit-scrollbar {
          width: 5px;
        }

        .main-content-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.1);
        }

        .main-content-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(79, 70, 229, 0.3);
          border-radius: 20px;
        }

        .main-content-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(79, 70, 229, 0.5);
        }
      `}</style>
    </div>
  );
};

export default Home;
